<template>
  <CCard class="staff-ui-card bg-white">
    <CRow class="">
      <CCol md="12">
        <CCardHeader class="staff-ui-header d-flex justify-content-between align-items-center">
          <h4 class="card-title mb-0 text-white">
            <CIcon name="cil-group" class="mr-2" /> {{ $t('staffMgmt.pageTitle') }}
          </h4>
          <div class="d-flex align-items-center gap-2">
            <CButton v-if="selectedUsers.length > 0" color="danger" @click="deleteMultipleUsers()"
              class="staff-ui-header-button staff-ui-header-button--danger">
              <CIcon name="cil-trash" /> <span class="d-none d-md-inline">{{ $t('staffMgmt.deleteSelected') }}</span> ({{ selectedUsers.length }})
            </CButton>
            <CButton class="staff-ui-header-button staff-ui-header-button--add" @click="openAddModal()">
              <CIcon name="cil-user-follow" />
              &nbsp;{{ $t('staffMgmt.addStaff') }}
            </CButton>
          </div>
        </CCardHeader>

        <CCardBody class="staff-ui-body">
          <p v-if="isLoading" role="status" class="text-muted">{{ $t('asyncFeedback.loading') }}</p>
          <div v-else-if="isError" role="alert" class="alert alert-danger">
            {{ errorMessage }}
            <button type="button" class="btn btn-outline-primary" @click="loadUsers">{{ $t('asyncFeedback.retry') }}</button>
          </div>

          <div class="staff-ui-summary" v-show="hasLoaded && ((!isLoading && !isError) || tableUsers.length)">
            <div><span>{{ $t('staffMgmt.scope') }}</span><strong>{{ $t('staffMgmt.allSections') }}</strong></div>
            <div class="staff-ui-count">{{ tableUsers.length }} {{ $t('staffMgmt.peopleUnit') }}</div>
          </div>
          <div class="staff-ui-mobile" v-show="hasLoaded && ((!isLoading && !isError) || tableUsers.length)">
            <div class="staff-ui-mobile-search">
              <CIcon name="cil-search" />
              <input v-model.trim="mobileSearch" type="search" :placeholder="$t('staffMgmt.searchPlaceholder')" @input="activePage = 1" />
            </div>
            <article v-for="item in mobilePageUsers" :key="item._id" class="staff-ui-mobile-card">
              <div class="staff-ui-mobile-card__top">
                <div><strong>{{ item.name }} {{ item.surname }}</strong><a :href="`mailto:${item.email}`">{{ item.email || '–' }}</a></div>
                <span class="staff-ui-role" :class="`staff-ui-role--${item.role}`">{{ roleLabel(item.role) }}</span>
              </div>
              <dl>
                <div><dt>{{ $t('staffMgmt.phone') }}</dt><dd>{{ item.mobile_phone || '–' }}</dd></div>
                <div><dt>{{ $t('staffMgmt.section') }}</dt><dd>{{ item.sectionNameDisplay || '–' }}</dd></div>
                <div><dt>{{ $t('staffMgmt.jobType') }}</dt><dd class="staff-ui-job">{{ item.jobTypeDisplay || '–' }}</dd></div>
              </dl>
              <div class="staff-ui-mobile-actions">
                <button type="button" class="staff-ui-action staff-ui-action--edit" @click="openEditModal(item)"><CIcon name="cil-pencil" /> {{ $t('mgmt.edit') }}</button>
                <button type="button" class="staff-ui-action staff-ui-action--delete" :class="{ 'staff-ui-action--disabled': item.activeTicketCount > 0 }" :disabled="item.activeTicketCount > 0" :title="item.activeTicketCount > 0 ? `${$t('mgmt.cannotDeleteActiveTasks')} (${item.activeTicketCount})` : $t('mgmt.delete')" @click="deleteUser(item)">
                  <CIcon name="cil-trash" /> {{ $t('mgmt.delete') }}
                  <span v-if="item.activeTicketCount > 0" class="staff-active-task-badge">{{ item.activeTicketCount }}</span>
                </button>
              </div>
            </article>
            <div v-if="mobilePageUsers.length === 0" class="staff-ui-mobile-empty">{{ $t('staffMgmt.noResults') }}</div>
            <div v-if="mobileTotalPages > 1" class="staff-ui-mobile-pagination">
              <button :disabled="activePage <= 1" @click="activePage--">‹</button><span>{{ activePage }} / {{ mobileTotalPages }}</span><button :disabled="activePage >= mobileTotalPages" @click="activePage++">›</button>
            </div>
          </div>
          <CDataTable :loading="isLoading" v-show="hasLoaded && ((!isLoading && !isError) || tableUsers.length)" class="custom-pagination staff-ui-desktop-table" :items="tableUsers" :fields="tableFields" hover striped sorter
            :pagination="{ doubleArrows: false, align: 'center' }" :items-per-page="perPage" :active-page="activePage"
            @page-change="activePage = $event" @pagination-change="perPage = $event"
            @filtered-items-change="onFilteredItemsChange"
            :table-filter="{ label: $t('table.filterLabel'), placeholder: $t('table.filterPlaceholder') }"
            :items-per-page-select="{ label: $t('table.itemsPerPageLabel') }" responsive>

            <template #select-header>
              <input type="checkbox" @change="toggleSelectAll($event.target.checked)" :checked="isPageSelected"
                style="cursor: pointer; width: 1.2em; height: 1.2em;" />
            </template>

            <template #select="{ item }">
              <td class="text-center align-middle">
                <input type="checkbox" :value="item._id" v-model="selectedUsers"
                  :disabled="item.activeTicketCount > 0"
                  :title="item.activeTicketCount > 0 ? `${$t('mgmt.cannotDeleteActiveTasks')} (${item.activeTicketCount})` : ''"
                  style="cursor: pointer; width: 1.2em; height: 1.2em;" />
              </td>
            </template>

            <template #email="{ item }">
              <td class="align-middle">
                <a class="staff-ui-email" :href="`mailto:${item.email}`" :title="item.email">{{ item.email || '–' }}</a>
              </td>
            </template>

            <template #mobile_phone="{ item }">
              <td class="align-middle"><span class="staff-phone">{{ item.mobile_phone || '–' }}</span></td>
            </template>

            <template #role="{ item }">
              <td class="align-middle"><span class="staff-ui-role" :class="`staff-ui-role--${item.role}`">{{ roleLabel(item.role) }}</span></td>
            </template>

            <template #sectionNameDisplay="{ item }">
              <td class="align-middle"><strong class="staff-ui-section">{{ item.sectionNameDisplay || '–' }}</strong></td>
            </template>

            <template #jobTypeDisplay="{ item }">
              <td class="align-middle"><span class="staff-ui-job">{{ item.jobTypeDisplay || '–' }}</span></td>
            </template>

            <template #actions="{ item }">
              <td class="text-center">
                <div class="d-flex justify-content-center align-items-center gap-2 action-buttons">
                  <CButton size="sm" class="staff-ui-action staff-ui-action--edit" :title="$t('mgmt.edit')" @click="openEditModal(item)">
                    <CIcon name="cil-pencil" />
                    <span>{{ $t('mgmt.edit') }}</span>
                  </CButton>
                  <CButton size="sm" class="staff-ui-action staff-ui-action--delete" 
                    :class="{ 'staff-ui-action--disabled': item.activeTicketCount > 0 }"
                    :disabled="item.activeTicketCount > 0"
                    :title="item.activeTicketCount > 0 ? `${$t('mgmt.cannotDeleteActiveTasks')} (${item.activeTicketCount})` : $t('mgmt.delete')" 
                    @click="deleteUser(item)">
                    <CIcon name="cil-trash" />
                    <span>{{ $t('mgmt.delete') }}</span>
                    <span v-if="item.activeTicketCount > 0" class="staff-active-task-badge" :title="`${item.activeTicketCount} ${$t('mgmt.itemsUnit')}`">
                      {{ item.activeTicketCount }}
                    </span>
                  </CButton>
                </div>
              </td>
            </template>
          </CDataTable>
        </CCardBody>
      </CCol>
    </CRow>

    <CModal size="lg" class="management-add-modal" centered :show.sync="showaddModal" :close-button="false">
      <template #header>
        <h5 class="modal-title mb-0 font-weight-bold">{{ $t('staffMgmt.addModal') }}</h5>
      </template>
      <CModalBody v-if="addUser" class="p-3">
        <CForm>
          <label>{{ $t('staffMgmt.firstName') }} <span class="text-danger">*</span></label>
          <CInput v-model="addUser.name" class="mb-3" />

          <label>{{ $t('staffMgmt.lastName') }} <span class="text-danger">*</span></label>
          <CInput v-model="addUser.surname" class="mb-3" />

          <label>{{ $t('staffMgmt.email') }} <span class="text-danger">*</span></label>
          <CInput v-model="addUser.email" class="mb-3" />

          <label>{{ $t('staffMgmt.phone') }} <span class="text-danger">*</span></label>
          <CInput v-model="addUser.mobile_phone" class="mb-3" />

          <label>{{ $t('staffMgmt.userType') }} <span class="text-danger">*</span></label>
          <CSelect :options="roleOptions" :value="addUser.role" @change="onChangeAddRole" class="mb-3" />

          <template v-if="['supervisor', 'technician'].includes(addUser.role)">
            <label>{{ $t('staffMgmt.section') }} <span class="text-danger">*</span></label>
            <CSelect :options="sectionOptions" :value="addUser.sectionId" @change="onChangeAddSection" class="mb-3" />
          </template>

          <template v-if="addUser.role === 'technician'">
            <label>{{ $t('staffMgmt.department') }} <span class="text-danger">*</span></label>
            <CSelect :options="departmentOptions" :value="addUser.departmentId"
              @change="onChangeAddDepartment" :disabled="!addUser.sectionId" class="mb-3" />
          </template>

          <template v-if="addUser.role === 'technician'">
            <label>{{ $t('staffMgmt.jobType') }} <span class="text-danger">*</span></label>
            <CSelect :options="getJobTypeOptions(addUser.sectionId, addUser.departmentId)" :value="addUser.jobType"
              @change="onChangeAddJobType" :disabled="!addUser.sectionId || !addUser.departmentId" class="mb-3" />
          </template>
        </CForm>
      </CModalBody>
      <template #footer>
        <div class="staff-modal-footer-wrapper">
          <div class="staff-modal-footer-import">
            <CButton class="management-modal-template admin-action-btn" @click="downloadTemplate">
              <CIcon name="cil-cloud-download" class="mr-1" /> {{ $t('staffMgmt.downloadTemplate') }}
            </CButton>
            <CButton class="management-modal-import admin-action-btn" @click="triggerFileUpload">
              <CIcon name="cil-cloud-upload" class="mr-1" /> {{ $t('staffMgmt.addViaXlsx') }}
            </CButton>
          </div>
          <div class="staff-modal-footer-actions">
            <CButton class="management-modal-cancel admin-action-btn" @click="showaddModal = false">
              {{ $t('mgmt.cancel') }}
            </CButton>
            <CButton class="management-modal-save admin-action-btn" @click="saveaddUser">
              {{ $t('mgmt.add') }}
            </CButton>
          </div>
        </div>
      </template>
    </CModal>
    <input type="file" ref="fileInput" accept=".xlsx, .xls" class="d-none" @change="handleFileUpload" />

    <CModal size="lg" class="management-edit-modal" centered :show.sync="showeditModal" :close-button="false">
      <template #header>
        <h5 class="modal-title mb-0 font-weight-bold">{{ $t('staffMgmt.editModal') }}</h5>
      </template>
      <CModalBody v-if="editUser">
        <CForm>
          <div class="p-1">
            <CRow>
              <CCol sm="6">
                <label>{{ $t('staffMgmt.firstName') }} <span class="text-danger">*</span></label>
                <CInput v-model="editUser.name" />
              </CCol>
              <CCol sm="6">
                <label>{{ $t('staffMgmt.lastName') }} <span class="text-danger">*</span></label>
                <CInput v-model="editUser.surname" />
              </CCol>
            </CRow>
            <CRow>
              <CCol sm="6">
                <label>{{ $t('staffMgmt.email') }} <span class="text-danger">*</span></label>
                <CInput v-model="editUser.email" />
              </CCol>
              <CCol sm="6">
                <label>{{ $t('staffMgmt.phone') }} <span class="text-danger">*</span></label>
                <CInput v-model="editUser.mobile_phone" />
              </CCol>
            </CRow>
            <CRow>
              <CCol sm="6">
                <CSelect :label="$t('staffMgmt.userType')" :options="roleOptions" :value="editUser.role"
                  @change="onChangeEditRole" />
              </CCol>
              <CCol sm="6" v-if="['supervisor', 'technician'].includes(editUser.role)">
                <label>{{ $t('staffMgmt.section') }}</label>
                <CSelect :options="sectionOptions" :value="editUser.sectionId" @change="onChangeEditSection" />
              </CCol>
              <CCol sm="6" v-if="editUser.role === 'technician'">
                <label>{{ $t('staffMgmt.department') }} <span class="text-danger">*</span></label>
                <CSelect :options="departmentOptions" :value="editUser.departmentId"
                  @change="onChangeEditDepartment" :disabled="!editUser.sectionId" />
              </CCol>
              <CCol sm="6" v-if="editUser.role === 'technician'">
                <label>{{ $t('staffMgmt.jobType') }} <span class="text-danger">*</span></label>
                <CSelect :options="getJobTypeOptions(editUser.sectionId, editUser.departmentId)" :value="editUser.jobType"
                  @change="onChangeEditJobType" :disabled="!editUser.sectionId || !editUser.departmentId" />
              </CCol>
            </CRow>
          </div>
        </CForm>
      </CModalBody>
      <template #footer>
        <CButton class="management-modal-cancel admin-action-btn" @click="showeditModal = false">
          {{ $t('mgmt.cancel') }}
        </CButton>
        <CButton class="management-modal-save admin-action-btn" @click="saveUser">
          {{ $t('mgmt.save') }}
        </CButton>
      </template>
    </CModal>

    <CModal size="xl" class="management-edit-modal" centered :show.sync="showPreviewModal" :close-button="false">
      <template #header>
        <h5 class="modal-title mb-0 font-weight-bold">{{ $t('staffMgmt.previewModal') }}</h5>
      </template>
      <CModalBody>
        <div class="preview-summary-card mb-3" v-if="previewUsers.length">
          <div class="preview-summary-text">
            <span>{{ $t('staffMgmt.previewSummary', { total: previewUsers.length, ready: readyPreviewCount, invalid: invalidPreviewCount }) }}</span>
            <small class="d-block text-muted mt-1">{{ $t('staffMgmt.onlyValidImported') }}</small>
          </div>
          <div>
            <CBadge color="success" class="mr-2 px-2 py-1">{{ $t('staffMgmt.readyToImport') }}: {{ readyPreviewCount }}</CBadge>
            <CBadge color="danger" class="px-2 py-1" v-if="invalidPreviewCount > 0">{{ $t('staffMgmt.invalidData') }}: {{ invalidPreviewCount }}</CBadge>
          </div>
        </div>

        <CDataTable :items="previewUsers" :fields="previewFields" hover striped responsive pagination :items-per-page="10">
          <template #status="{ item }">
            <td class="text-center align-middle">
              <CBadge :color="item.isValid ? 'success' : 'danger'" class="preview-status-badge">
                {{ item.statusText }}
              </CBadge>
              <small v-if="!item.isValid && item.errorReason" class="d-block text-danger mt-1 text-left" style="font-size: 0.75rem;">
                {{ item.errorReason }}
              </small>
            </td>
          </template>
          <template #role="{ item }">
            <td class="align-middle">
              <CBadge :color="item.role === 'admin' ? 'dark' : item.role === 'supervisor' ? 'warning' : item.role === 'technician' ? 'info' : 'secondary'">
                {{ roleLabel(item.role) }}
              </CBadge>
            </td>
          </template>
        </CDataTable>
      </CModalBody>
      <template #footer>
        <CButton class="management-modal-cancel admin-action-btn" @click="showPreviewModal = false">
          {{ $t('mgmt.cancel') }}
        </CButton>
        <CButton class="management-modal-save admin-action-btn" :disabled="readyPreviewCount === 0" @click="confirmUpload">
          <CIcon name="cil-check" class="mr-1" /> {{ $t('staffMgmt.confirmBulk') }} ({{ readyPreviewCount }})
        </CButton>
      </template>
    </CModal>
  </CCard>
</template>

<script>
import CModal from '@/components/common/AccessibleModal.vue';
import { getDepartmentJobTypes } from '@/utils/staffJobTypeOptions';
import { assertListResponse, queueLatestListLoad, disposeListLoad } from "@/utils/asyncFeedback";
import axios from "axios";
import api from "@/service/api";
import Swal from "sweetalert2";
import { formatAdminSwalButton } from "@/utils/sweetalertButtonHelper";
import * as XLSX from "xlsx";
import { translateSection, translateDepartment, translateJobType } from "@/utils/categoryi18n";


export default {
  components: { CModal },
  data() {
    return {
      selectedUsers: [],
      tableUsers: [],
      showaddModal: false,
      showeditModal: false,
      addUser: null,
      editUser: null,
      roleOptions: [
        { value: "admin", label: "Admin" },
        { value: "student_affairs", label: "ส่วนพัฒนานักศึกษา / Student Affairs" },
        { value: "supervisor", label: "Supervisor" },
        { value: "technician", label: "Technician" },
      ],
      categoryOptions: [],
      sections: [],
      sectionOptions: [],
      departmentOptions: [],
      departmentOptionsSectionId: "",
      isLoading: false,
      hasLoaded: false,
      isError: false,
      errorMessage: "",
      showPreviewModal: false,
      previewUsers: [],
      activePage: 1,
      perPage: 10,
      mobileSearch: "",
      filteredUsers: [],
    };
  },
  computed: {
    mobileFilteredUsers() {
      const keyword = this.mobileSearch.toLocaleLowerCase();
      if (!keyword) return this.tableUsers;
      return this.tableUsers.filter(item => [item.name, item.surname, item.email, item.mobile_phone, item.role, item.sectionNameDisplay, item.jobTypeDisplay]
        .some(value => String(value || "").toLocaleLowerCase().includes(keyword)));
    },
    mobilePageUsers() {
      const start = (this.activePage - 1) * this.perPage;
      return this.mobileFilteredUsers.slice(start, start + this.perPage);
    },
    mobileTotalPages() {
      return Math.max(1, Math.ceil(this.mobileFilteredUsers.length / this.perPage));
    },
    tableFields() {
      return [
        { key: "select", label: "", _style: "width: 1%; text-align: center;", sorter: false, filter: false },
        { key: "name", label: this.$t("staffMgmt.firstName") },
        { key: "surname", label: this.$t("staffMgmt.lastName") },
        { key: "email", label: this.$t("staffMgmt.email") },
        { key: "mobile_phone", label: this.$t("staffMgmt.phone") },
        { key: "role", label: this.$t("staffMgmt.userType") },
        { key: "sectionNameDisplay", label: this.$t("staffMgmt.section"), _style: "min-width: 190px;" },
        { key: "jobTypeDisplay", label: this.$t("staffMgmt.jobType") },
        { key: "actions", label: this.$t("mgmt.actions"), _style: "text-align: center; width: 145px; min-width: 145px;", sorter: false, filter: false },
      ];
    },
    currentPageUsers() {
      const start = (this.activePage - 1) * this.perPage;
      const end = start + this.perPage;
      return this.filteredUsers.slice(start, end);
    },
    isPageSelected() {
      const selectableUsers = this.currentPageUsers.filter(u => !(u.activeTicketCount > 0));
      if (selectableUsers.length === 0) return false;
      return selectableUsers.every(u => this.selectedUsers.includes(u._id));
    },
    previewFields() {
      return [
        { key: "status", label: this.$t("staffMgmt.status"), _style: "min-width: 120px; text-align: center;" },
        { key: "name", label: this.$t("staffMgmt.firstName"), _style: "min-width: 90px;" },
        { key: "surname", label: this.$t("staffMgmt.lastName"), _style: "min-width: 90px;" },
        { key: "email", label: this.$t("staffMgmt.email"), _style: "min-width: 170px;" },
        { key: "mobile_phone", label: this.$t("staffMgmt.phone"), _style: "min-width: 110px;" },
        { key: "role", label: this.$t("staffMgmt.userType"), _style: "min-width: 100px; text-align: center;" },
        { key: "sectionNameDisplay", label: this.$t("staffMgmt.section"), _style: "min-width: 150px;" },
        { key: "deptNameDisplay", label: this.$t("staffMgmt.department"), _style: "min-width: 180px;" },
        { key: "jobTypeDisplay", label: this.$t("staffMgmt.jobType"), _style: "min-width: 170px;" },
      ];
    },
    readyPreviewCount() {
      return this.previewUsers.filter(u => u.isValid).length;
    },
    invalidPreviewCount() {
      return this.previewUsers.filter(u => !u.isValid).length;
    },
  },
  mounted() {
    this.loadSections();
    this.loadUsers();
  },
  beforeDestroy() {
    disposeListLoad(this);
  },
  methods: {
    roleLabel(role) {
      return role === "student_affairs" ? (this.$store.state.locale.lang === "en" ? "Student Affairs" : "ส่วนพัฒนานักศึกษา") : role;
    },
    async loadSections() {
      try {
        const res = await axios.get(`${process.env.VUE_APP_APP_URL}/category`);
        const data = res.data.data || [];
        this.sections = data.filter(item =>
          item.isActive !== false &&
          Array.isArray(item.subCategories) &&
          item.subCategories.some(sub => sub.isActive !== false)
        );
        const lang = this.$store?.state?.locale?.lang || "th";
        this.sectionOptions = [
          { value: "", label: this.$t("staffMgmt.selectSection") },
          ...this.sections.map((item) => ({ value: item._id, label: translateSection(item.departmentName || item.name, lang) })),
        ];
        // backward compatible options used by CSV import and other places
        this.categoryOptions = [
          { value: "", label: this.$t("staffMgmt.selectDept") },
          { value: "all", label: this.$t("staffMgmt.allDepts") },
          ...data.map((item) => ({ value: item._id, label: translateSection(item.departmentName || item.name, lang) })),
        ];
      } catch (err) {
        console.error("Error loading sections:", err);
      }
    },

    getJobTypeOptions(sectionId, departmentId) {
      if (!sectionId || !departmentId) {
        return [{ value: "", label: this.$t("staffMgmt.selectJobType"), disabled: true }];
      }
      const lang = this.$store?.state?.locale?.lang || "th";
      const section = this.sections.find(item => String(item._id) === String(sectionId));
      const departments = this.departmentOptionsSectionId === sectionId ? this.departmentOptions : [];
      const jobTypes = getDepartmentJobTypes(section, departmentId, departments);
      return [
        { value: "", label: this.$t("staffMgmt.selectJobType"), disabled: true },
        ...jobTypes.map(item => ({ value: item.name, label: translateJobType(item.name, lang) })),
      ];
    },
    async loadDepartments(sectionId) {
      const requestId = this._departmentOptionsRequestId = (this._departmentOptionsRequestId || 0) + 1;
      const isCurrent = () => !this._isDestroyed && !this._isBeingDestroyed && this._departmentOptionsRequestId === requestId;
      this.departmentOptionsSectionId = sectionId || "";
      this.departmentOptions = [{ value: "", label: this.$t("staffMgmt.selectDept"), disabled: true }];
      if (!sectionId) {
        return;
      }
      try {
        const lang = this.$store?.state?.locale?.lang || "th";
        const res = await api.department("get", null, { params: { section_id: sectionId } });
        if (!isCurrent()) return;
        const departments = (res.data.data || []).filter(d => d.isActive !== false);
        this.departmentOptions = [
          { value: "", label: this.$t("staffMgmt.selectDept"), disabled: true },
          ...departments.map(d => ({ value: d._id, label: translateDepartment(d.name, lang) })),
        ];
        if (departments.length === 1) {
          if (this.editUser && (!this.editUser.departmentId || !departments.some(d => String(d._id) === String(this.editUser.departmentId)))) {
            this.editUser.departmentId = departments[0]._id;
          }
          if (this.addUser && (!this.addUser.departmentId || !departments.some(d => String(d._id) === String(this.addUser.departmentId)))) {
            this.addUser.departmentId = departments[0]._id;
          }
        }
      } catch (err) {
        if (!isCurrent()) return;
        console.error("Error loading departments:", err);
        this.departmentOptions = [{ value: "", label: this.$t("staffMgmt.selectDept"), disabled: true }];
      }
    },
    async onChangeAddSection(event) {
      const val = event?.target ? event.target.value : (event || "");
      this.addUser.sectionId = val;
      this.addUser.departmentId = "";
      this.addUser.jobType = "";
      await this.loadDepartments(val);
    },
    async onChangeEditSection(event) {
      const val = event?.target ? event.target.value : (event || "");
      this.editUser.sectionId = val;
      this.editUser.departmentId = "";
      this.editUser.jobType = "";
      await this.loadDepartments(val);
    },
    onChangeAddDepartment(event) {
      const val = event?.target ? event.target.value : (event || "");
      this.addUser.departmentId = val;
      this.addUser.jobType = "";
    },
    onChangeEditDepartment(event) {
      const val = event?.target ? event.target.value : (event || "");
      this.editUser.departmentId = val;
      this.editUser.jobType = "";
    },
    onChangeAddJobType(e) {
      this.addUser.jobType = e && e.target ? e.target.value : (e || "");
    },
    onChangeEditJobType(e) {
      this.editUser.jobType = e && e.target ? e.target.value : (e || "");
    },
    loadUsers() {
      return queueLatestListLoad(this, async isCurrent => {
        try {
          this.isError = false;
          this.errorMessage = "";
          // Load the complete staff list once and let CDataTable own filtering,
          // page size and pagination. Combining server-side limit=5 with local
          // pagination left the table with only one page of data.
          const res = await axios.get(`${process.env.VUE_APP_APP_URL}/user/admin_user`);
          if (!isCurrent()) return;
          assertListResponse(res);
          const items = res.data.items || res.data.data || [];

          const lang = this.$store?.state?.locale?.lang || "th";
          this.tableUsers = items.map(user => {
            const rawSec = user.section?.departmentName || user.section?.name;
            const secDisplay = user.role === "admin" ? "–" : (rawSec ? translateSection(rawSec, lang) : "–");
            const rawJob = user.jobType;
            const jobDisplay = user.role === "technician"
              ? (rawJob ? translateJobType(rawJob, lang) : this.$t("staffMgmt.jobTypeUnassigned"))
              : "–";
            return {
              ...user,
              mobile_phone: user.mobile_phone || "",
              sectionNameDisplay: secDisplay,
              jobTypeDisplay: jobDisplay,
            };
          });
          this.filteredUsers = JSON.parse(JSON.stringify(this.tableUsers));
          this.hasLoaded = true;
        } catch (err) {
          if (!isCurrent()) return;
          this.isError = true;
          this.errorMessage = this.$t(err.response?.status === 403 ? "mgmt.accessDenied" : "mgmt.loadFail");
          if (err.response?.status === 403) {
            Swal.fire(this.$t("mgmt.error"), this.$t("mgmt.accessDenied"), "error");
          } else {
            console.error("Error loading users:", err);
          }
        }
      });
    },
    openAddModal() {
      this.departmentOptions = [{ value: "", label: this.$t("staffMgmt.selectDept") }];
      this.addUser = {
        name: "",
        surname: "",
        email: "",
        mobile_phone: "",
        role: "technician",
        sectionId: "",
        departmentId: "",
        jobType: "",
      };
      this.showaddModal = true;
    },
    async onChangeAddRole(e) {
      const val = e?.target ? e.target.value : (e || "");
      this.addUser.role = val;
      if (["admin", "student_affairs", "supervisor"].includes(this.addUser.role)) {
        this.addUser.jobType = "";
      } else if (this.addUser.role === "technician" && this.addUser.sectionId) {
        await this.loadDepartments(this.addUser.sectionId);
      }
    },
    async onChangeEditRole(e) {
      const val = e?.target ? e.target.value : (e || "");
      this.editUser.role = val;
      if (["admin", "student_affairs", "supervisor"].includes(this.editUser.role)) {
        this.editUser.jobType = "";
      } else if (this.editUser.role === "technician" && this.editUser.sectionId) {
        await this.loadDepartments(this.editUser.sectionId);
      }
    },
    async saveaddUser() {
      const emailVal = this.addUser.email ? this.addUser.email.trim() : "";
      if (
        !this.addUser.name ||
        !this.addUser.surname ||
        !emailVal ||
        !this.addUser.mobile_phone ||
        !this.addUser.role ||
        (["supervisor", "technician"].includes(this.addUser.role) && !this.addUser.sectionId) ||
        (this.addUser.role === "technician" && !this.addUser.departmentId) ||
        (this.addUser.role === "technician" && !this.addUser.jobType)
      ) {
        Swal.fire(this.$t("mgmt.error"), this.$t("mgmt.errFillAll"), "error");
        return;
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(emailVal)) {
        Swal.fire(this.$t("mgmt.error"), this.$t("staffMgmt.invalidEmailFormat"), "error");
        return;
      }
      try {
        const payload = {
          name: this.addUser.name,
          surname: this.addUser.surname,
          email: emailVal,
          mobile_phone: this.addUser.mobile_phone,
          role: this.addUser.role,
          sectionId: this.addUser.sectionId || undefined,
          departmentId: this.addUser.role === "technician" ? this.addUser.departmentId : undefined,
          jobType: this.addUser.role === "technician" ? this.addUser.jobType : null,
        };

        const res = await axios.post(`${process.env.VUE_APP_APP_URL}/user/admin_user`, payload);
        if (res.data?.success === false) {
          Swal.fire(this.$t("mgmt.error"), res.data.message, "error");
          return;
        }
        Swal.fire(this.$t("mgmt.success"), res.data.message, "success");
        this.showaddModal = false;
        this.loadUsers();
      } catch (err) {
        Swal.fire(
          this.$t("mgmt.error"),
          err.response?.data?.message || this.$t("mgmt.addFail"),
          "error"
        );
      }
    },
    openEditModal(item) {
      this.editUser = JSON.parse(JSON.stringify(item));
      // Staff are scoped by responsible section; technicians also use job type.
      this.$set(this.editUser, "sectionId", item.section?._id || item.section || item.sectionId || "");
      this.$set(this.editUser, "departmentId", item.department?._id || item.department || item.departmentId || "");
      this.$set(this.editUser, "jobType", item.jobType || "");
      this.showeditModal = true;
      if (this.editUser.sectionId) {
        this.loadDepartments(this.editUser.sectionId);
      }
    },
    async saveUser() {
      const emailVal = this.editUser.email ? this.editUser.email.trim() : "";
      if (
        !this.editUser.name ||
        !this.editUser.surname ||
        !emailVal ||
        !this.editUser.mobile_phone ||
        !this.editUser.role ||
        (["supervisor", "technician"].includes(this.editUser.role) && !this.editUser.sectionId) ||
        (this.editUser.role === "technician" && !this.editUser.departmentId) ||
        (this.editUser.role === "technician" && !this.editUser.jobType)
      ) {
        Swal.fire(this.$t("mgmt.error"), this.$t("mgmt.errFillAll"), "error");
        return;
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(emailVal)) {
        Swal.fire(this.$t("mgmt.error"), this.$t("staffMgmt.invalidEmailFormat"), "error");
        return;
      }
      try {

        const payload = {
          _id: this.editUser._id,
          name: this.editUser.name,
          surname: this.editUser.surname,
          email: emailVal,
          mobile_phone: this.editUser.mobile_phone,
          role: this.editUser.role,
          sectionId: ["supervisor", "technician"].includes(this.editUser.role) ? (this.editUser.sectionId || null) : null,
          departmentId: this.editUser.role === "technician" ? (this.editUser.departmentId || null) : null,
          jobType: this.editUser.role === "technician" ? this.editUser.jobType : null,
        };

        const res = await axios.put(`${process.env.VUE_APP_APP_URL}/user/admin_user`, payload);
        if (res.data?.success === false) {
          Swal.fire(this.$t("mgmt.error"), res.data.message || this.$t("mgmt.editFail"), "error");
          return;
        }

        Swal.fire(this.$t("mgmt.success"), this.$t("mgmt.editSaved"), "success");
        this.showeditModal = false;
        this.loadUsers();
      } catch (err) {
        const errorMsg = err.response?.data?.message || this.$t("mgmt.editFail");
        Swal.fire(this.$t("mgmt.error"), errorMsg, "error");
      }
    },
    async deleteUser(item) {
      if (item.activeTicketCount > 0) {
        Swal.fire({
          title: this.$t("mgmt.cannotDelete"),
          text: `${this.$t("mgmt.cannotDeleteActiveTasks")} (${item.activeTicketCount} ${this.$t("mgmt.itemsUnit")})`,
          icon: "warning",
          confirmButtonText: formatAdminSwalButton('check', this.$t("mgmt.confirm")),
        });
        return;
      }

      const confirm = await Swal.fire({
        title: this.$t("mgmt.deleteUserTitle"),
        text: `${item.name} ${item.surname}`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: formatAdminSwalButton('trash', this.$t("mgmt.confirm")),
        cancelButtonText: formatAdminSwalButton('ban', this.$t("mgmt.cancel")),
      });
      if (!confirm.isConfirmed) return;

      const confirm2 = await Swal.fire({
        title: this.$t("mgmt.sureDelete"),
        text: `${item.name} ${item.surname} — ${this.$t("mgmt.demoteUserSuffix")}`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: formatAdminSwalButton('trash', this.$t("mgmt.confirmDelete")),
        cancelButtonText: formatAdminSwalButton('ban', this.$t("mgmt.cancel")),
      });
      if (!confirm2.isConfirmed) return;

      try {
        const res = await axios.put(`${process.env.VUE_APP_APP_URL}/user/del_user`, item);
        if (res.data) {
          Swal.fire(this.$t("mgmt.success"), this.$t("mgmt.demoteSuccess"), "success");

          // เอาออกจากที่เลือกไว้ด้วยเผื่อว่าถูกลบไปแล้ว
          this.selectedUsers = this.selectedUsers.filter(id => id !== item._id);
          this.loadUsers();
        }
      } catch (err) {
        const errMsg = err.response?.data?.message || err.response?.data?.msg || (err.response?.status === 403 ? this.$t("mgmt.accessDenied") : this.$t("mgmt.deleteUserFailed"));
        Swal.fire(this.$t("mgmt.error"), errMsg, "error");
      }
    },
    onFilteredItemsChange(items) {
      this.filteredUsers = items;
    },
    toggleSelectAll(checked) {
      const selectablePageIds = this.currentPageUsers
        .filter(u => !(u.activeTicketCount > 0))
        .map(u => u._id);
      if (checked) {
        const newSelected = [...this.selectedUsers];
        selectablePageIds.forEach(id => {
          if (!newSelected.includes(id)) {
            newSelected.push(id);
          }
        });
        this.selectedUsers = newSelected;
      } else {
        const allCurrentPageIds = this.currentPageUsers.map(u => u._id);
        this.selectedUsers = this.selectedUsers.filter(id => !allCurrentPageIds.includes(id));
      }
    },
    async deleteMultipleUsers() {
      const confirm = await Swal.fire({
        title: this.$t("mgmt.deleteUserBulkTitle"),
        text: `${this.$t("mgmt.countItems")} ${this.selectedUsers.length} ${this.$t("mgmt.itemsUnit")}`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: formatAdminSwalButton('trash', this.$t("mgmt.confirm")),
        cancelButtonText: formatAdminSwalButton('ban', this.$t("mgmt.cancel")),
      });
      if (!confirm.isConfirmed) return;

      const confirm2 = await Swal.fire({
        title: this.$t("mgmt.sureDelete"),
        text: `${this.$t("mgmt.demoteUsersBulk")}`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: formatAdminSwalButton('trash', this.$t("mgmt.confirmDelete")),
        cancelButtonText: formatAdminSwalButton('ban', this.$t("mgmt.cancel")),
      });
      if (!confirm2.isConfirmed) return;

      Swal.fire({
        title: this.$t("mgmt.loadingDelete"),
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      let successCount = 0;
      let failCount = 0;

      for (const userId of this.selectedUsers) {
        try {
          const res = await axios.put(`${process.env.VUE_APP_APP_URL}/user/del_user`, { _id: userId });
          if (res.data) successCount++;
        } catch (err) {
          failCount++;
        }
      }

      Swal.fire({
        title: failCount === 0 ? this.$t("mgmt.success") : this.$t("mgmt.doneTitle"),
        html: `${this.$t("mgmt.batchDeleted")} ${successCount} ${this.$t("mgmt.itemsUnit")}` + (failCount > 0 ? `<br>${this.$t("mgmt.batchFailed")} ${failCount} ${this.$t("mgmt.itemsUnit")}` : ""),
        icon: failCount === 0 ? "success" : "warning"
      });

      this.selectedUsers = [];
      this.loadUsers();
    },
    downloadTemplate() {
      const link = document.createElement("a");
      link.href = "/filexlsx/Template Add Staff _ SmartFix MFU.xlsx";
      link.setAttribute("download", "Template Add Staff _ SmartFix MFU.xlsx");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    triggerFileUpload() {
      this.$refs.fileInput.click();
    },
    async handleFileUpload(event) {
      const file = event.target.files[0];
      if (!file) return;

      Swal.fire({
        title: this.$t("mgmt.loadingRead"),
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      // Load all departments for cross-section mapping
      let allDepts = [];
      try {
        const deptRes = await api.department("get");
        allDepts = (deptRes.data?.data || []).filter(d => d.isActive !== false);
      } catch (err) {
        console.warn("Could not load departments for excel parser:", err);
      }

      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: "array" });
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet);

          const parsedUsers = [];

          for (const rawRow of jsonData) {
            const row = {};
            for (const [k, v] of Object.entries(rawRow)) {
              const clean = String(k || "").replace(/[*()（）]/g, "").trim();
              row[clean] = v;
              row[clean.toLowerCase()] = v;
              row[clean.replace(/\s+/g, "").toLowerCase()] = v;
            }

            const name = String(row["ชื่อ"] || row["ชื่อจริง"] || row["first name"] || row["firstname"] || row["name"] || "").trim();
            const surname = String(row["นามสกุล"] || row["last name"] || row["lastname"] || row["surname"] || "").trim();
            const email = String(row["อีเมล"] || row["อีเมล์"] || row["email"] || "").trim();

            // Skip instruction/guide rows and empty rows
            if (name.startsWith("คำแนะนำ") || name.startsWith("หมายเหตุ") || (!name && !surname && !email) || (!email && !surname)) continue;

            let mobile = row["เบอร์โทรศัพท์"] || row["เบอร์โทร"] || row["เบอร์"] || row["phone"] || row["mobile"] || row["tel"] || "";
            if (typeof mobile === "number") {
              mobile = Math.floor(mobile).toString();
              if (mobile.length === 9) mobile = "0" + mobile;
            } else {
              mobile = String(mobile || "").trim();
            }

            let roleRaw = String(row["ประเภทผู้ใช้"] || row["สิทธิ์"] || row["ตำแหน่ง"] || row["role"] || row["usertype"] || row["user type"] || "").toLowerCase().trim();
            let roleStr = "technician";
            if (roleRaw.includes("admin") || roleRaw.includes("ผู้ดูแลระบบ")) {
              roleStr = "admin";
            } else if (roleRaw.includes("super") || roleRaw.includes("หัวหน้า")) {
              roleStr = "supervisor";
            } else if (roleRaw.includes("affairs") || roleRaw.includes("พัฒนานักศึกษา")) {
              roleStr = "student_affairs";
            } else if (roleRaw.includes("tech") || roleRaw.includes("ช่าง") || roleRaw === "technicain") {
              roleStr = "technician";
            }

            const cleanInput = (val) => {
              const s = String(val || "").trim();
              return s === "-" || s === "–" || s === "—" || s.toLowerCase() === "null" || s.toLowerCase() === "undefined" ? "" : s;
            };

            // Input section & department resolution
            const rawSectionInput = cleanInput(row["ฝ่ายรับผิดชอบงาน"] || row["ส่วนงาน"] || row["ฝ่าย"] || row["section"]);
            const rawDeptInput = cleanInput(row["หน่วยงาน"] || row["ฝ่าย"] || row["แผนก"] || row["department"]);

            let sectionId = "";
            let sectionNameDisplay = "-";
            let deptId = "";
            let deptNameDisplay = "-";
            let jobTypeVal = "";
            let jobTypeDisplay = "-";

            if (["admin", "student_affairs"].includes(roleStr)) {
              sectionId = "";
              sectionNameDisplay = "-";
              deptId = "";
              deptNameDisplay = "-";
              jobTypeVal = "";
              jobTypeDisplay = "-";
            } else {
              // 1. Try to match section
              let matchedSection = null;
              if (rawSectionInput) {
                matchedSection = this.sections.find(s =>
                  String(s.departmentName || "").toLowerCase() === rawSectionInput.toLowerCase() ||
                  String(s.name || "").toLowerCase() === rawSectionInput.toLowerCase() ||
                  String(s.code || "").toLowerCase() === rawSectionInput.toLowerCase()
                );
              }

              // If section not found from rawSectionInput, check rawDeptInput as section
              if (!matchedSection && rawDeptInput) {
                matchedSection = this.sections.find(s =>
                  String(s.departmentName || "").toLowerCase() === rawDeptInput.toLowerCase() ||
                  String(s.name || "").toLowerCase() === rawDeptInput.toLowerCase() ||
                  String(s.code || "").toLowerCase() === rawDeptInput.toLowerCase()
                );
              }

              // If still not matched, check if rawDeptInput matches a department in allDepts
              if (!matchedSection && rawDeptInput) {
                const matchedDeptInAll = allDepts.find(d =>
                  String(d.name || "").toLowerCase() === rawDeptInput.toLowerCase()
                );
                if (matchedDeptInAll) {
                  const secIdFromDept = matchedDeptInAll.section?._id || matchedDeptInAll.section;
                  matchedSection = this.sections.find(s => String(s._id) === String(secIdFromDept));
                  deptId = matchedDeptInAll._id;
                  deptNameDisplay = matchedDeptInAll.name;
                }
              }

              if (matchedSection) {
                sectionId = matchedSection._id;
                sectionNameDisplay = matchedSection.departmentName || matchedSection.name;

                // 2. Resolve department if role is technician
                if (roleStr === "technician" && !deptId) {
                  const sectionDepts = allDepts.filter(d => {
                    const secId = d.section?._id || d.section;
                    return String(secId) === String(sectionId);
                  });

                  const matchedDept = sectionDepts.find(d =>
                    String(d.name || "").toLowerCase() === rawDeptInput.toLowerCase()
                  );

                  if (matchedDept) {
                    deptId = matchedDept._id;
                    deptNameDisplay = matchedDept.name;
                  } else if (sectionDepts.length === 1) {
                    deptId = sectionDepts[0]._id;
                    deptNameDisplay = sectionDepts[0].name;
                  } else if (rawDeptInput) {
                    deptNameDisplay = rawDeptInput;
                  }
                }

                // 3. Resolve job type if technician
                if (roleStr === "technician") {
                  const rawJobType = cleanInput(row["ประเภทงานช่าง"] || row["ประเภทงานของช่าง"] || row["ประเภทงาน"] || row["jobtype"] || row["job type"]);
                  const subCats = (matchedSection.subCategories || []).filter(sub => sub.isActive !== false);

                  if (rawJobType) {
                    const matchedJob = subCats.find(j =>
                      String(j.name || "").toLowerCase() === rawJobType.toLowerCase()
                    );
                    if (matchedJob) {
                      jobTypeVal = matchedJob.name;
                      jobTypeDisplay = matchedJob.name;
                    } else {
                      jobTypeVal = rawJobType;
                      jobTypeDisplay = rawJobType;
                    }
                  } else if (subCats.length === 1) {
                    jobTypeVal = subCats[0].name;
                    jobTypeDisplay = subCats[0].name;
                  }
                }
              }
            }

            // Skip completely empty rows
            if (!name && !surname && !email) continue;

            // Row validation
            const errors = [];
            if (!name) errors.push(this.$t("staffMgmt.firstName") + " จำเป็น");
            if (!surname) errors.push(this.$t("staffMgmt.lastName") + " จำเป็น");
            if (!email) {
              errors.push(this.$t("staffMgmt.email") + " จำเป็น");
            } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
              errors.push("รูปแบบอีเมลไม่ถูกต้อง");
            }

            if (["supervisor", "technician"].includes(roleStr) && !sectionId) {
              errors.push("ไม่พบฝ่ายรับผิดชอบงานที่ตรงกัน");
            }

            if (roleStr === "technician") {
              if (!deptId) errors.push("ไม่พบหน่วยงาน/ฝ่ายที่ตรงกัน");
              if (!jobTypeVal) errors.push("ไม่พบประเภทงานช่างที่ตรงกัน");
            }

            parsedUsers.push({
              name,
              surname,
              email,
              mobile_phone: mobile,
              role: roleStr,
              sectionId: sectionId,
              sectionNameDisplay: sectionNameDisplay,
              departmentId: deptId,
              deptNameDisplay: deptNameDisplay,
              jobType: jobTypeVal,
              jobTypeDisplay: jobTypeDisplay,
              isValid: errors.length === 0,
              errorReason: errors.join(", "),
              statusText: errors.length === 0 ? this.$t("staffMgmt.readyToImport") : this.$t("staffMgmt.invalidData")
            });
          }

          this.previewUsers = parsedUsers;
          this.showPreviewModal = true;
          Swal.close();

          event.target.value = null;
        } catch (error) {
          console.error(error);
          Swal.fire(this.$t("mgmt.error"), this.$t("mgmt.fileReadError"), "error");
          event.target.value = null;
        }
      };

      reader.readAsArrayBuffer(file);
    },
    async confirmUpload() {
      const validUsers = this.previewUsers.filter(u => u.isValid);
      if (validUsers.length === 0) {
        Swal.fire(this.$t("mgmt.noData"), this.$t("mgmt.noDataToSave"), "warning");
        return;
      }

      Swal.fire({
        title: this.$t("mgmt.loadingSave"),
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      let successCount = 0;
      let failCount = 0;
      const errors = [];

      for (const user of validUsers) {
        const payload = {
          name: user.name,
          surname: user.surname,
          email: user.email,
          mobile_phone: user.mobile_phone,
          role: user.role,
          sectionId: user.sectionId || undefined,
          departmentId: user.role === "technician" ? user.departmentId : undefined,
          jobType: user.role === "technician" ? user.jobType : null,
        };

        try {
          const res = await axios.post(`${process.env.VUE_APP_APP_URL}/user/admin_user`, payload);
          if (res.data && res.data.success !== false) {
            successCount++;
          } else {
            failCount++;
            errors.push(`${user.name}: ${res.data.message || 'Failed'}`);
          }
        } catch (apiErr) {
          failCount++;
          errors.push(`${user.name}: ${apiErr.response?.data?.message || apiErr.message}`);
        }
      }

      let msg = `${this.$t("mgmt.batchAdded")} ${successCount} ${this.$t("mgmt.itemsUnit")}`;
      if (failCount > 0) {
        msg += `<br>${this.$t("mgmt.batchFailed")} ${failCount} ${this.$t("mgmt.itemsUnit")}`;
        console.warn("Upload errors:", errors);
      }

      Swal.fire({
        title: failCount === 0 ? this.$t("mgmt.doneTitle") : this.$t("mgmt.donePartial"),
        html: msg,
        icon: failCount === 0 ? "success" : "warning"
      });

      this.showPreviewModal = false;
      this.showaddModal = false;
      this.previewUsers = [];
      this.loadUsers();
    },
  },
};
</script>

<style lang="scss">
.staff-management-card {
  overflow: hidden;
  border: 1px solid rgba(37, 99, 235, 0.12) !important;
  border-radius: 18px !important;
  box-shadow: 0 12px 36px rgba(45, 26, 26, 0.08) !important;
}

.staff-management-header {
  min-height: 88px;
  padding: 20px 28px !important;
  border: 0 !important;
  background: #1e40af !important;
}

.staff-management-header .card-title {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: clamp(1.35rem, 2vw, 1.8rem);
  font-weight: 800;
}

.staff-header-button {
  min-height: 46px;
  padding: 10px 18px !important;
  border-radius: 12px !important;
  font-weight: 800 !important;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.14);
}

.staff-header-button--add {
  color: #1e40af !important;
  border: 1px solid #fff !important;
  background: #fff !important;
}

.staff-header-button--add:hover {
  background: #f0f9ff !important;
}

.staff-management-body {
  padding: 24px 28px 30px !important;
}

.staff-context {
  display: flex;
  align-items: baseline;
  gap: 10px;
  margin-bottom: 18px;
  color: #1e40af;
  font-size: 1.1rem;
}

.staff-context-label {
  color: #737985;
  font-weight: 600;
}

.staff-context strong {
  font-size: 1.22rem;
  font-weight: 800;
}

.staff-management-card .table-responsive {
  border: 1px solid rgba(37, 99, 235, 0.12);
  border-radius: 14px;
}

.staff-management-card table {
  margin-bottom: 0;
}

.staff-management-card thead th {
  padding: 15px 14px !important;
  color: #1e40af;
  background: #f0f9ff !important;
  border-bottom: 1px solid rgba(37, 99, 235, 0.14) !important;
  font-weight: 800;
  white-space: nowrap;
}

.staff-management-card tbody td {
  padding: 15px 14px !important;
  color: #343b4a;
  border-color: rgba(37, 99, 235, 0.08) !important;
  vertical-align: middle !important;
}

.staff-management-card .table-striped tbody tr:nth-of-type(odd) {
  background: #f8fafc;
}

.staff-management-card tbody tr:hover {
  background: #f0f9ff !important;
}

.staff-email {
  display: inline-block;
  max-width: 260px;
  overflow: hidden;
  color: #465267;
  font-weight: 600;
  text-decoration: none;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.staff-email:hover {
  color: #1e40af;
  text-decoration: underline;
}

.staff-role {
  display: inline-flex;
  padding: 5px 11px;
  border-radius: 999px;
  color: #1e40af;
  background: #e0f2fe;
  font-size: .84rem;
  font-weight: 800;
  text-transform: capitalize;
}

.staff-role--technician { color: #315170; background: #eaf2f8; }
.staff-role--supervisor { color: #705000; background: #fff4d6; }
.staff-role--admin { color: #1e40af; background: #e0f2fe; }
.staff-section { color: #424955; font-weight: 800; }
.staff-job { color: #1e40af; font-weight: 700; }
.staff-phone { white-space: nowrap; }

.action-buttons {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  flex-wrap: nowrap;
}

.staff-management-card .form-control,
.staff-management-card .custom-select,
.management-edit-modal .form-control,
.management-edit-modal .custom-select,
.management-add-modal .form-control,
.management-add-modal .custom-select {
  min-height: 44px;
  height: 44px;
  padding: 8px 14px;
  font-size: 16px;
  line-height: 1.5;
  border: 1px solid rgba(37, 99, 235, 0.2);
  border-radius: 10px;
}

.staff-management-card .form-control:focus,
.staff-management-card .custom-select:focus,
.management-edit-modal .form-control:focus,
.management-edit-modal .custom-select:focus,
.management-add-modal .form-control:focus,
.management-add-modal .custom-select:focus {
  border-color: #1e40af;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
}

.gap-2 {
  gap: 0.5rem;
}



.modal-header {
  color: #ffffff !important;
  background: #1e40af !important;
}

@media (max-width: 768px) {
  .staff-management-header {
    align-items: flex-start !important;
    flex-direction: column;
    gap: 14px;
    padding: 18px !important;
  }

  .staff-management-header > div,
  .staff-header-button {
    width: 100%;
  }

  .staff-management-body {
    padding: 16px 12px 22px !important;
  }

  .staff-context {
    align-items: flex-start;
    flex-direction: column;
    gap: 2px;
  }

  .staff-management-card .table-responsive {
    border-radius: 12px;
  }

  .staff-action span {
    display: none;
  }
}
</style>
